import { useCallback, useEffect, useState } from "react";
import { uniqueUUID } from "../../utils";

/**
 * Normalize a row for dirty-check snapshot (strip key so row identity doesn't affect comparison).
 */
function snapshotRow<R extends { key: string }>(
  r: R
): Omit<R, "key"> & { key: "" } {
  return { ...r, key: "" } as Omit<R, "key"> & { key: "" };
}

/**
 * Generic hook for section-edit modals: row state, reset on open, JSON-snapshot dirty tracking,
 * and add/remove/update row callbacks. Use with PersonEditSectionModal for consistent UX.
 */
function getInitialRowsAndSnapshot<R extends { key: string }>(
  initialFormRows: R[],
  getEmptyRow: () => R
): { rows: R[]; initialSnapshot: string } {
  const rows =
    initialFormRows.length > 0
      ? initialFormRows.map((r) => ({ ...r, key: r.key || uniqueUUID() }))
      : [getEmptyRow()];
  return {
    rows,
    initialSnapshot: JSON.stringify(rows.map(snapshotRow)),
  };
}

export function useRowForm<R extends { key: string }>(
  open: boolean,
  initialFormRows: R[],
  getEmptyRow: () => R,
  onDirtyChange: (dirty: boolean) => void
) {
  const [{ rows, initialSnapshot }, setState] = useState<{
    rows: R[];
    initialSnapshot: string;
  }>(() => getInitialRowsAndSnapshot(initialFormRows, getEmptyRow));

  const setRows = useCallback((value: R[] | ((prev: R[]) => R[])) => {
    setState((prev) => ({
      ...prev,
      rows: typeof value === "function" ? value(prev.rows) : value,
    }));
  }, []);

  useEffect(() => {
    if (open) {
      const { rows: rowsToSet, initialSnapshot: snapshotToSet } =
        getInitialRowsAndSnapshot(initialFormRows, getEmptyRow);
      setState({ rows: rowsToSet, initialSnapshot: snapshotToSet });
    }
  }, [open, initialFormRows, getEmptyRow]);

  const currentSnapshot = JSON.stringify(rows.map(snapshotRow));
  const hasUnsaved = currentSnapshot !== initialSnapshot;
  useEffect(() => {
    onDirtyChange(hasUnsaved);
  }, [hasUnsaved, onDirtyChange]);

  const updateRow = useCallback(
    (idx: number, field: keyof R, value: string) => {
      setRows((prev) => {
        const next = [...prev];
        if (idx < 0 || idx >= next.length) return prev;
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });
    },
    [setRows]
  );

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, getEmptyRow()]);
  }, [getEmptyRow, setRows]);

  const removeRow = useCallback(
    (idx: number) => {
      setRows((prev) =>
        prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)
      );
    },
    [setRows]
  );

  return { rows, setRows, updateRow, addRow, removeRow };
}
